import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "components/Layout";
import EmphasisText from "components/EmphasisText";
import LeonardoLanguageToggle from "components/LeonardoLanguageToggle";
import api from "utils/api";
import { formatPrice } from "constants/massages";
import { useLeonardoLocale } from "scenes/leonardoNikolai/LeonardoLocaleContext";
import "styles/booking.css";

const POLL_MS = 2000;
const MAX_POLLS = 30;
const BERLIN_TZ = "Europe/Berlin";

const WAKEUP_SERVICE_LABELS = {
  "dr-hoffmann-wakeup-studio": "Wake-Up im Studio",
  "dr-hoffmann-wakeup-onsite": "Wake-Up vor Ort",
  "dr-hoffmann-wakeup-onsite-voucher": "Wake-Up vor Ort",
  "dr-hoffmann-wakeup-plus-business": "Wake-Up+ für Unternehmen",
  "dr-hoffmann-classic-studio": "Classic Massage im Studio",
  "dr-hoffmann-classic-onsite": "Classic Massage vor Ort",
  "dr-hoffmann-premium-studio": "Intense Massage im Studio",
  "dr-hoffmann-premium-onsite": "Intense Massage vor Ort",
};

const HOTEL_SERVICE_KEYS = new Set([
  "dr-hoffmann-hotel-15",
  "dr-hoffmann-hotel-60",
]);

const bookingMatches = (booking, { offerType, serviceKey, label }) =>
  booking?.offerType === offerType ||
  booking?.serviceKey === serviceKey ||
  booking?.hoffmannBookingOption === label ||
  booking?.serviceLabel === label;

const bookingPriceBreakdown = (booking) => {
  const netCents = Number(booking?.netCents ?? booking?.netAmountCents);
  const vatCents = Number(booking?.vatCents ?? booking?.taxCents);
  const grossCents = Number(
    booking?.grossCents ?? booking?.totalCents ?? booking?.priceCents
  );
  if (![netCents, vatCents, grossCents].every(Number.isFinite)) return null;
  return { netCents, vatCents, grossCents };
};

const formatServiceAddress = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  return (
    addr.fullAddress ||
    addr.address ||
    [addr.street, [addr.postalCode, addr.city].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", ")
  );
};

const isHotelBooking = (booking) =>
  HOTEL_SERVICE_KEYS.has(booking?.serviceKey) ||
  booking?.source === "leonardo-nikolai" ||
  booking?.source === "leonardo-nyx";

const parseHotelServiceNotes = (notes) => ({
  floor: String(notes || "").match(/^(?:Stockwerk|Floor):\s*(.+)$/im)?.[1]?.trim() || "",
  room: String(notes || "").match(/^(?:Zimmer|Room):\s*(.+)$/im)?.[1]?.trim() || "",
});

const bookingGuestName = (booking) => {
  const customerName = [booking?.customer?.firstName, booking?.customer?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (customerName) return customerName;
  return String(booking?.serviceAddress?.companyName || "").trim();
};

const hotelServiceLabel = (booking, t) => {
  if (booking?.serviceKey === "dr-hoffmann-hotel-15") {
    return t("options.hotel-quick-15.title");
  }
  if (booking?.serviceKey === "dr-hoffmann-hotel-60") {
    return t("options.hotel-performance-60.title");
  }
  return booking?.hoffmannBookingOption || booking?.serviceLabel || "";
};

const BookingSuccessPage = () => {
  const [params] = useSearchParams();
  const location = useLocation();
  const token = useSelector((s) => s.token);
  const { shellT, isLeonardoFlow, dateLocale } = useLeonardoLocale();
  const bookingId = params.get("bookingId");
  const sessionId = params.get("session_id");
  const initialBooking = location.state?.booking || null;
  const [booking, setBooking] = useState(initialBooking);
  const [error, setError] = useState(null);
  // Stripe redirects here before the webhook/confirm finishes — start in "confirming".
  const [confirmingPayment, setConfirmingPayment] = useState(Boolean(sessionId));

  const isHotel = isHotelBooking(booking);
  const leonardoHotel = isLeonardoFlow && isHotel;
  const leonardoBookingPath =
    booking?.source === "leonardo-nyx" ? "/leonardo-nyx" : "/leonardo-nikolai";

  const formatDateTime = useCallback(
    (date) =>
      new Date(date).toLocaleString(leonardoHotel ? dateLocale : "de-DE", {
        timeZone: BERLIN_TZ,
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [dateLocale, leonardoHotel]
  );

  useEffect(() => {
    if (!bookingId) return undefined;
    if (!token && !sessionId) return undefined;
    let cancelled = false;
    let attempts = 0;

    const stillAwaitingPayment = (b) =>
      b?.status === "pending-payment" ||
      b?.status === "reserved-awaiting-payment" ||
      b?.status === "awaiting-immediate-payment";

    const load = async () => {
      try {
        let confirmedBooking = null;

        if (attempts === 0 && sessionId) {
          try {
            const { data: confirmData } = await api.post(
              `/payment/confirm/${bookingId}`,
              { sessionId }
            );
            confirmedBooking = confirmData?.booking || null;
            if (confirmedBooking) {
              setBooking(confirmedBooking);
              setError(null);
              if (!stillAwaitingPayment(confirmedBooking)) {
                setConfirmingPayment(false);
                // Manual / guest payment links: confirm response is enough.
                // Do not call GET /bookings/:id (often 404 when not linked to this login).
                if (!token || confirmedBooking.manual || !confirmedBooking.user) {
                  return;
                }
              }
            }
          } catch {
            /* webhook may still arrive; fall through to owned-booking poll when logged in */
          }
        }

        if (!token) {
          // Guest success redirect: payment email + webhook already handle the rest.
          setConfirmingPayment(false);
          setError(null);
          return;
        }

        const { data } = await api.get(`/bookings/${bookingId}`);
        if (cancelled) return;
        const b = data.booking || confirmedBooking || initialBooking;
        setBooking(b);
        setError(null);

        if (stillAwaitingPayment(b) && attempts < MAX_POLLS) {
          if (!sessionId) {
            try {
              await api.post(`/payment/confirm/${bookingId}`, {});
            } catch {
              /* webhook may still arrive */
            }
            const { data: refreshed } = await api.get(`/bookings/${bookingId}`);
            if (cancelled) return;
            const updated = refreshed.booking || b;
            setBooking(updated);
            if (!stillAwaitingPayment(updated)) {
              setConfirmingPayment(false);
              return;
            }
          }
          setConfirmingPayment(true);
          attempts += 1;
          window.setTimeout(load, POLL_MS);
        } else {
          setConfirmingPayment(false);
          if (stillAwaitingPayment(b)) {
            setError(
              leonardoHotel
                ? shellT("success.paymentPending")
                : "Die Zahlung wird noch bestätigt. Bitte prüfe in ein paar Minuten dein Konto oder kontaktiere uns."
            );
          }
        }
      } catch (err) {
        if (cancelled) return;
        // Stripe success redirects often land here for manual bookings that are
        // not owned by the current login — payment already succeeded via webhook.
        if (sessionId) {
          setConfirmingPayment(false);
          setError(null);
          return;
        }
        if (!initialBooking) {
          setConfirmingPayment(false);
          setError(
            err?.response?.data?.message ||
              (leonardoHotel ? shellT("success.notFound") : "Buchung nicht gefunden.")
          );
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [bookingId, token, initialBooking, leonardoHotel, shellT, sessionId]);

  const isPendingEmailVerification =
    booking?.status === "pending-email-verification";
  const isFirm =
    booking &&
    booking.status !== "pending-payment" &&
    booking.status !== "reserved-awaiting-payment" &&
    booking.status !== "awaiting-immediate-payment" &&
    booking.status !== "expired" &&
    booking.status !== "cancelled";
  const isStarterPackage =
    booking?.hoffmannBookingOption === "Starterpaket buchen";
  const isBusinessEvent =
    booking?.serviceKey === "dr-hoffmann-business-event" ||
    booking?.hoffmannBookingOption === "WM-Aktion" ||
    booking?.hoffmannBookingOption === "Business-Event";
  const isWakeupOnsite = bookingMatches(booking, {
    offerType: "wakeup_onsite",
    serviceKey: "dr-hoffmann-wakeup-onsite",
    label: "Wake-Up vor Ort",
  }) || bookingMatches(booking, {
    offerType: "wakeup_onsite_voucher",
    serviceKey: "dr-hoffmann-wakeup-onsite-voucher",
    label: "Wake-Up vor Ort",
  });
  const isWakeupPlus = bookingMatches(booking, {
    offerType: "wakeup_plus_business",
    serviceKey: "dr-hoffmann-wakeup-plus-business",
    label: "Wake-Up+ für Unternehmen",
  });
  const isLeonardoWakeupOnsite = leonardoHotel && isWakeupOnsite;
  const displayServiceLabel = leonardoHotel
    ? hotelServiceLabel(booking, shellT)
    : booking?.hoffmannBookingOption ||
      WAKEUP_SERVICE_LABELS[booking?.serviceKey] ||
      booking?.serviceLabel;
  const parsedHotelNotes = parseHotelServiceNotes(booking?.serviceAddress?.notes);
  const hotelDetails = isHotel
    ? {
        visitLocation: String(booking?.serviceAddress?.visitLocation || "").trim(),
        floor:
          parsedHotelNotes.floor ||
          String(booking?.serviceAddress?.floor || "").trim(),
        room:
          parsedHotelNotes.room ||
          String(booking?.serviceAddress?.roomNumber || "").trim(),
      }
    : null;
  const displayLocation = isHotel
    ? ""
    : booking?.hoffmannBookingOption === "Lizenzpartner werden"
      ? "Cremon 11, 20457 Hamburg"
      : booking?.location || formatServiceAddress(booking?.serviceAddress);
  const guestName = isHotel ? bookingGuestName(booking) : "";
  const priceBreakdown = bookingPriceBreakdown(booking);
  const displayPriceBreakdown = isLeonardoWakeupOnsite ? null : priceBreakdown;
  const displayPriceCents = Number(booking?.priceCents || 0);
  const hasVoucher = Boolean(booking?.hasVoucher || booking?.requiresVoucherPresentation);

  const isAwaitingPaymentStatus =
    booking?.status === "pending-payment" ||
    booking?.status === "reserved-awaiting-payment" ||
    booking?.status === "awaiting-immediate-payment";

  const isAwaitingPaymentConfirmation =
    confirmingPayment ||
    isAwaitingPaymentStatus ||
    (Boolean(sessionId) &&
      Boolean(token) &&
      !isFirm &&
      booking?.status !== "cancelled" &&
      booking?.status !== "expired");

  const summaryMessage = useMemo(() => {
    if (leonardoHotel) {
      if (isAwaitingPaymentConfirmation) return shellT("success.confirmingPayment");
      if (isPendingEmailVerification) return shellT("success.pendingEmail");
      if (isFirm && booking?.status === "pending-employee-confirmation") {
        return shellT("success.pendingEmployee");
      }
      if (isFirm) return shellT("success.confirmed");
      if (booking?.status === "cancelled") return shellT("success.failed");
      return shellT("success.confirmingPayment");
    }
    if (isAwaitingPaymentConfirmation) {
      return "Deine Zahlung wird bestätigt — gleich ist dein Termin fest gebucht.";
    }
    if (isPendingEmailVerification) {
      return "Dein Termin ist vorgemerkt. Bitte bestätige deine E-Mail-Adresse über den Link in deinem Postfach. Erst danach wird die Buchung verbindlich und unser Team informiert.";
    }
    if (isFirm && booking?.status === "pending-employee-confirmation") {
      return "Deine Buchungsanfrage ist eingegangen und wartet auf die Bestätigung durch unser Team. Du erhältst eine weitere E-Mail, sobald der Termin bestätigt ist.";
    }
    if (isFirm) return "Deine Buchung ist bestätigt. Eine E-Mail mit allen Details ist unterwegs.";
    if (booking?.status === "cancelled") {
      return "Die Buchung konnte nicht abgeschlossen werden.";
    }
    if (sessionId) {
      return "Deine Zahlung ist eingegangen. Eine Bestätigungs-E-Mail mit allen Details ist unterwegs.";
    }
    return "Deine Zahlung wird bestätigt — gleich ist dein Termin fest gebucht.";
  }, [
    booking?.status,
    isAwaitingPaymentConfirmation,
    isFirm,
    isPendingEmailVerification,
    leonardoHotel,
    shellT,
    sessionId,
  ]);

  const paymentLabel = useMemo(() => {
    if (leonardoHotel) {
      if (booking?.paymentMethod === "stripe" && booking?.paymentStatus === "paid") {
        return shellT("success.paymentPaidOnline");
      }
      if (booking?.paymentMethod === "later") return shellT("success.paymentFreeFirst");
      if (booking?.paymentMethod === "stripe") return shellT("success.paymentOnline");
      return shellT("success.paymentOnSite");
    }
    if (booking?.paymentMethod === "stripe" && booking?.paymentStatus === "paid") {
      return "Online (bezahlt)";
    }
    if (booking?.paymentMethod === "later") return "Kostenfrei (Erstbesuch)";
    if (booking?.paymentMethod === "stripe") return "Online";
    return "Vor Ort";
  }, [booking?.paymentMethod, booking?.paymentStatus, leonardoHotel, shellT]);

  return (
    <Layout>
      <section className="booking-page">
        <div className="option-detail" style={{ marginTop: 0 }}>
          {isLeonardoFlow && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
              <LeonardoLanguageToggle />
            </div>
          )}
          <h2>
            {leonardoHotel ? (
              <EmphasisText text={shellT("success.title")} />
            ) : (
              <>Vielen <em>Dank!</em></>
            )}
          </h2>
          <p className="step-summary">{summaryMessage}</p>
          {isPendingEmailVerification && (
            <div className="auth-alert is-info" style={{ marginBottom: "1rem" }}>
              {leonardoHotel
                ? shellT("success.verifyHint")
                : "Ohne E-Mail-Bestätigung findet der Termin nicht statt. Prüfe auch deinen Spam-Ordner."}
            </div>
          )}

          {error && (
            <div className="auth-alert is-error" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          {booking && isFirm && (
            <div
              className="booking-summary-block"
              style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}
            >
              <div className="summary-row">
                <span>{leonardoHotel ? shellT("success.service") : "Leistung"}</span>
                <strong>{displayServiceLabel}</strong>
              </div>
              {!isBusinessEvent && (
                <div className="summary-row">
                  <span>{leonardoHotel ? shellT("success.duration") : "Dauer"}</span>
                  <strong>
                    {leonardoHotel
                      ? shellT("success.durationValue", { minutes: booking.durationMinutes })
                      : `${booking.durationMinutes} Min.`}
                  </strong>
                </div>
              )}
              {booking.personCount ? (
                <div className="summary-row">
                  <span>{leonardoHotel ? shellT("success.persons") : "Personen"}</span>
                  <strong>{booking.personCount}</strong>
                </div>
              ) : null}
              <div className="summary-row">
                <span>{leonardoHotel ? shellT("success.appointment") : "Termin"}</span>
                <strong>
                  {isBusinessEvent
                    ? "14.06.2026 - Ab 16 Uhr - 19 Uhr Anpfiff"
                    : isStarterPackage
                      ? leonardoHotel
                        ? shellT("success.byArrangement")
                        : "Nach Vereinbarung"
                      : formatDateTime(booking.start)}
                </strong>
              </div>
              {isHotel ? (
                <>
                  {hotelDetails?.visitLocation ? (
                    <div className="summary-row">
                      <span>{leonardoHotel ? shellT("summary.visitLocation") : "Besuchsort"}</span>
                      <strong>
                        {hotelDetails.visitLocation === "wellness"
                          ? shellT("form.visitLocation.wellness")
                          : shellT("form.visitLocation.hotel")}
                      </strong>
                    </div>
                  ) : null}
                  {guestName ? (
                    <div className="summary-row">
                      <span>{leonardoHotel ? shellT("success.name") : "Name"}</span>
                      <strong>{guestName}</strong>
                    </div>
                  ) : null}
                  {hotelDetails?.floor ? (
                    <div className="summary-row">
                      <span>{leonardoHotel ? shellT("success.floor") : "Stockwerk"}</span>
                      <strong>{hotelDetails.floor}</strong>
                    </div>
                  ) : null}
                  {hotelDetails?.room ? (
                    <div className="summary-row">
                      <span>{leonardoHotel ? shellT("success.room") : "Zimmer"}</span>
                      <strong>{hotelDetails.room}</strong>
                    </div>
                  ) : null}
                </>
              ) : (
                displayLocation && (
                  <div className="summary-row">
                    <span>{leonardoHotel ? shellT("success.location") : "Ort"}</span>
                    <strong>{displayLocation}</strong>
                  </div>
                )
              )}
              <div className="summary-row">
                <span>{leonardoHotel ? shellT("success.price") : "Preis"}</span>
                <strong>
                  {displayPriceBreakdown
                    ? formatPrice(displayPriceBreakdown.grossCents)
                    : displayPriceCents === 0
                      ? leonardoHotel
                        ? shellT("success.free")
                        : "Kostenfrei"
                      : formatPrice(displayPriceCents)}
                </strong>
              </div>
              {displayPriceBreakdown && !isBusinessEvent && (
                <>
                  <div className="summary-row">
                    <span>{leonardoHotel ? shellT("success.net") : "Netto"}</span>
                    <strong>{formatPrice(displayPriceBreakdown.netCents)}</strong>
                  </div>
                  <div className="summary-row">
                    <span>{leonardoHotel ? shellT("success.vat") : "MwSt. 19 %"}</span>
                    <strong>{formatPrice(displayPriceBreakdown.vatCents)}</strong>
                  </div>
                  <div className="summary-row">
                    <span>{leonardoHotel ? shellT("success.gross") : "Brutto"}</span>
                    <strong>{formatPrice(displayPriceBreakdown.grossCents)}</strong>
                  </div>
                </>
              )}
              <div className="summary-row">
                <span>{leonardoHotel ? shellT("success.status") : "Status"}</span>
                <strong>
                  {booking.status === "pending-employee-confirmation"
                    ? leonardoHotel
                      ? shellT("success.statusPending")
                      : "Wartet auf Bestätigung"
                    : leonardoHotel
                      ? shellT("success.statusConfirmed")
                      : "Bestätigt"}
                </strong>
              </div>
              <div className="summary-row">
                <span>{leonardoHotel ? shellT("success.payment") : "Zahlung"}</span>
                <strong>{paymentLabel}</strong>
              </div>
              {isWakeupOnsite && !leonardoHotel && (
                <div className="auth-alert is-success" style={{ marginTop: "1rem" }}>
                  {hasVoucher ? (
                    <>
                      Bitte bringe deinen Gutschein zum Termin mit. Anfahrt: 0 € statt 39 €.
                    </>
                  ) : (
                    "Anfahrt: 39 €."
                  )}
                </div>
              )}
              {isWakeupPlus && (
                <div className="auth-alert is-success" style={{ marginTop: "1rem" }}>
                  Wake-Up+ für 10 Mitarbeiter inkl. Anfahrt.
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: "2rem", display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
            {leonardoHotel ? (
              <>
                <Link to={leonardoBookingPath} className="btn-primary">
                  {shellT("success.backToLeonardo")}
                </Link>
                {token ? (
                  <Link to="/konto" className="btn-outline">
                    {shellT("success.accountCta")}
                  </Link>
                ) : null}
              </>
            ) : token ? (
              <Link to="/konto" className="btn-primary">Zu meinem Konto</Link>
            ) : (
              <Link to="/buchen" className="btn-primary">Weitere Optionen</Link>
            )}
            <Link to="/" className="btn-ghost">
              {leonardoHotel ? shellT("success.homeCta") : "Zur Startseite"}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BookingSuccessPage;
